<template>
    <div class="circle-tile" @click="!disabled && $emit('click')">
        <!-- pillMode: název jako pilulka NAD čtvercem, kroky jako pilulka POD
             čtvercem (CityTravelDialog) - vychozí varianta má obojí přímo na
             obrázku (CityNext grid). -->
        <div v-if="pillMode" class="circle-tile-stack">
            <div class="circle-tile-name-slot">
                <div class="circle-tile-name-pill">
                    {{ title }}
                    <div v-if="belowLabel" class="circle-tile-below-pill">{{ belowLabel }}</div>
                </div>
            </div>
            <!-- obrázek a odznak jsou sourozenci, ne vnořené - CSS `filter`
                 (greyscale) na rodiči by se propsal i na potomka bez ohledu
                 na jeho vlastní třídu -->
            <div class="circle-tile-avatar-wrap">
                <v-img
                    :src="img"
                    :aspect-ratio="1"
                    :class="{'greyscale': imgGreyscale || disabled}"
                    class="circle-tile-avatar"
                    cover
                />
                <v-img
                    v-if="badgeImg"
                    :src="badgeImg"
                    :class="{'greyscale': badgeGreyscale}"
                    class="circle-tile-badge"
                    contain
                />
                <v-icon v-else-if="cornerIcon" class="circle-tile-corner-icon" :color="cornerIconColor" size="28">{{ cornerIcon }}</v-icon>
                <v-icon v-if="showStar" class="circle-tile-star-icon" color="#FFD700" size="19">mdi-star</v-icon>
                <v-img
                    v-if="gymBadgeImg"
                    :src="gymBadgeImg"
                    :class="{'greyscale': !gymBadgeOwned}"
                    class="circle-tile-gym-badge"
                    contain
                />
                <div v-if="order !== null" class="circle-tile-order-badge">{{ order }}</div>
            </div>
            <div v-if="steps !== null && steps > 0" class="circle-tile-steps-pill circle-tile-steps-pill--below">
                <template v-if="steps > 1">{{ steps }}</template> <v-icon small color="black" class="ml-0">mdi-shoe-print</v-icon>
            </div>
        </div>

        <div v-else class="circle-tile-avatar-wrap">
            <v-img
                :src="img"
                :aspect-ratio="1"
                :class="{'greyscale': imgGreyscale || disabled}"
                class="circle-tile-avatar"
                cover
            />
            <v-img v-if="overlayImg" :src="overlayImg" contain class="circle-tile-overlay-img"/>
            <div class="circle-tile-overlay">
                <div class="circle-tile-title">{{ title }}</div>
            </div>
            <div
                v-if="steps !== null && steps > 0"
                class="circle-tile-steps-corner"
                :class="{'circle-tile-steps-corner--shifted': badgeImg || cornerIcon}"
            >
                <span v-if="steps > 1">{{ steps }}</span> <v-icon color="white" class="circle-tile-steps-icon">mdi-shoe-print</v-icon>
            </div>
            <v-img
                v-if="badgeImg"
                :src="badgeImg"
                :class="{'greyscale': badgeGreyscale}"
                class="circle-tile-badge"
                contain
            />
            <v-icon v-else-if="cornerIcon" class="circle-tile-corner-icon" :color="cornerIconColor" size="28">{{ cornerIcon }}</v-icon>
            <v-icon v-if="showStar" class="circle-tile-star-icon" color="#FFD700" size="19">mdi-star</v-icon>
        </div>
    </div>
</template>

<script>
    // Sdílená dlaždice pro journey/next - používá se v CityTravelDialog i
    // CityNext gridu. Čtverec přes `aspect-ratio="1"` (fluidní 100 % sloupce,
    // ne pevný px rozměr). Design viz claude/components/journey-city.md.
    export default {
        name: 'NextCircleTile',
        props: {
            img: {type: String, required: true},
            title: {type: String, required: true},
            // počet kroků - pilulka se ukáže jen když je > 0
            steps: {type: Number, default: null},
            imgGreyscale: {type: Boolean, default: false},
            badgeImg: {type: String, default: ''},
            badgeGreyscale: {type: Boolean, default: false},
            // mdi ikona vpravo nahoře, alternativa k `badgeImg` - když je
            // nastavený `badgeImg`, ten má přednost
            cornerIcon: {type: String, default: ''},
            cornerIconColor: {type: String, default: 'white'},
            // druhý obrázek "překryv" nad základním - jen ne-pillMode varianta
            overlayImg: {type: String, default: ''},
            // hvězdička v levém horním rohu (roadmap.md bod 5) - samostatný roh
            // od badgeImg/cornerIcon (ty jsou vpravo nahoře)
            showStar: {type: Boolean, default: false},
            // odznak stadionu cílového města vlevo dole (CityTravelDialog) -
            // jen v pillMode variantě, barva/šedivost řízená čistě vlastnictvím
            // (gymBadgeOwned), nezávisle na `disabled`/`imgGreyscale` karty
            gymBadgeImg: {type: String, default: ''},
            gymBadgeOwned: {type: Boolean, default: false},
            disabled: {type: Boolean, default: false},
            pillMode: {type: Boolean, default: false},
            // pořadové číslo v pravém dolním rohu čtverce (CityTravelDialog) -
            // null (default) = nic se nevykreslí
            order: {type: Number, default: null},
            // Pilulka POD čtvercem s libovolným textem (CityTravelDialog -
            // dlaždice aktuálního města: "You're here"). Má přednost před
            // steps pilulkou (stejný slot).
            belowLabel: {type: String, default: ''},
        },
    }
</script>

<style scoped>
.circle-tile {
    cursor: pointer;
    width: 100%;
    -webkit-tap-highlight-color: transparent;
}
.circle-tile-stack {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}
/* Obrázek, textový překryv i odznak jsou uvnitř sourozenci (viz komentář
   v template), aby `filter: grayscale` na samotném obrázku (.circle-tile-avatar)
   neovlivnil i je. */
.circle-tile-avatar-wrap {
    position: relative;
    width: 100%;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 2px 3px 8px rgba(0,0,0,0.5);
    transition: box-shadow 0.12s ease;
    /* container query kontext pro .circle-tile-title - dlaždice je fluidní
       (procentuální sloupec), `cqw` váže font na skutečnou šířku téhle
       konkrétní dlaždice (ne na šířku viewportu jako `vw`). */
    container-type: inline-size;
}
.circle-tile-avatar {
    width: 100%;
}
/* Jen :active (skutečný tap/klik), NE :hover se scale/translate - změna
   velikosti/pozice terče během hoveru dělala na dotykových zařízeních
   problém s trefením cíle. Jen box-shadow "prohloubení". */
.circle-tile:active .circle-tile-avatar-wrap {
    box-shadow: 0px 0px 2px rgba(0,0,0,0.4);
}
.circle-tile-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 8px 8px 10px 8px;
    background: linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.6) 45%, rgba(0,0,0,0.0) 100%);
    text-align: center;
}
.circle-tile-overlay-img {
    position: absolute;
    top: 6%;
    left: 6%;
    width: 88%;
    height: 88%;
    pointer-events: none;
    filter: drop-shadow(1px 2px 4px rgba(0,0,0,0.6));
}
.circle-tile-title {
    color: #fff;
    /* skaluje se s reálnou šířkou dlaždice (viz `container-type` na
       .circle-tile-avatar-wrap výše) */
    font-size: clamp(10px, 11cqw, 26px);
    font-weight: 700;
    line-height: 1.2;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.9);
}
.circle-tile-name-slot {
    /* pevná výška = placeholder pro VŽDY 2 řádky textu, i když se titulek
       vejde na 1 řádek - jinak různě dlouhé názvy dělaly čtverce různě
       vysoké/posunuté v gridu. Záporný margin-bottom stahuje čtverec nahoru -
       `transform` na pilulce nižší měření layoutu neovlivňuje, takže bez
       tohodle zůstávala mezi řádky mrtvá prázdná mezera. */
    width: 100%;
    height: 46px;
    margin-bottom: -24px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}
.circle-tile-name-pill {
    position: relative;
    z-index: 2;
    display: block;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
    padding: 3px 10px;
    border-radius: 10px;
    max-width: 100%;
    text-align: center;
    transform: translateY(35%);
}
.circle-tile-steps-pill {
    display: inline-block;
    background: #FFA726;
    color: #000;
    /* skaluje se s dlaždicí (container query, viz .circle-tile-avatar-wrap) */
    font-size: clamp(23px, 14cqw, 36px);
    font-weight: 700;
    padding: 2px 11px;
    border-radius: 14px;
    margin-top: 4px;
}
.circle-tile-steps-icon {
    /* stejný clamp jako .circle-tile-steps-corner - ikona 2x větší než titulek */
    font-size: clamp(20px, 22cqw, 52px) !important;
    filter: drop-shadow(1px 1px 2px rgba(0,0,0,0.9));
}
/* Vnořená přímo do .circle-tile-name-pill (vlastní position:relative) -
   `top: 100%` = automaticky hned pod spodní hranou titulku, ať je titulek
   jakkoliv vysoký/široký. Absolutní pozice = mimo tok, neovlivňuje velikost
   rodiče ani nic dalšího v layoutu. */
.circle-tile-below-pill {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 3px;
    z-index: 2;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 8px;
    white-space: nowrap;
}
.circle-tile-steps-pill--below {
    position: relative;
    z-index: 2;
    font-size: 34px;
    padding: 4px 18px;
    border-radius: 16px;
    margin-top: -16px;
    transform: translateY(-40%);
}
/* Kroky jako bílé číslo+ikona se stínem (žádný pevný podklad, funguje na
   libovolné fotce), stejná konvence jako .circle-tile-corner-icon. Když je
   "1", číslo se nepíše, jen ikona. */
.circle-tile-steps-corner {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    gap: 2px;
    color: #fff;
    font-weight: 700;
    /* 2x .circle-tile-title */
    font-size: clamp(20px, 22cqw, 52px);
    text-shadow: 1px 1px 2px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9);
    pointer-events: none;
}
/* když už je v rohu badge/cornerIcon, kroky se posunou pod něj, ať se nepřekrývají */
.circle-tile-steps-corner--shifted {
    top: 32%;
}
.circle-tile-badge {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 26%;
    height: 26%;
    flex: none;
    filter: drop-shadow(1px 2px 3px rgba(0,0,0,0.6));
}
/* zlatý glow na už získaném odznaku, jen když NENÍ greyscale - přetéká
   `.circle-tile-badge.greyscale` pravidlo níže díky pořadí v souboru
   (greyscale definováno až POTOM). */
.circle-tile-badge:not(.greyscale) {
    filter: drop-shadow(1px 2px 3px rgba(0,0,0,0.6)) drop-shadow(0 0 5px rgba(255,193,7,0.95)) drop-shadow(0 0 9px rgba(255,193,7,0.7));
}
.circle-tile-corner-icon {
    position: absolute !important;
    top: 4px;
    right: 4px;
    filter: drop-shadow(1px 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 0 2px rgba(0,0,0,0.9));
}
/* `filter` je jedna hodnota, ne slučitelné vlastnosti - obecná třída
   ".greyscale" (filter: grayscale(100%)) prohrává proti scoped
   ".circle-tile-badge" (vyšší specificita díky [data-v-xxx]), takže odznak
   zůstával barevný, i když měl být šedivý. Kombinované pravidlo pro oba
   filtry najednou, když jsou obě třídy na stejném elementu. */
.circle-tile-badge.greyscale {
    filter: grayscale(100%) drop-shadow(1px 2px 3px rgba(0,0,0,0.6));
}
/* Odznak cílového města vlevo dole (zrcadlově k circle-tile-order-badge
   vpravo dole) - NE vlevo nahoře, tam přesahuje pilulka s názvem města
   (circle-tile-name-pill) a odznak by se s textem překrýval. Stejná
   konvence glow/greyscale jako circle-tile-badge. */
.circle-tile-gym-badge {
    position: absolute;
    bottom: 6px;
    left: 6px;
    width: 26%;
    height: 26%;
    flex: none;
    filter: drop-shadow(1px 2px 3px rgba(0,0,0,0.6));
}
.circle-tile-gym-badge:not(.greyscale) {
    filter: drop-shadow(1px 2px 3px rgba(0,0,0,0.6)) drop-shadow(0 0 5px rgba(255,193,7,0.95)) drop-shadow(0 0 9px rgba(255,193,7,0.7));
}
.circle-tile-gym-badge.greyscale {
    filter: grayscale(100%) drop-shadow(1px 2px 3px rgba(0,0,0,0.6));
}
.circle-tile-star-icon {
    position: absolute !important;
    top: 4px;
    left: 4px;
    filter: drop-shadow(1px 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 0 2px rgba(0,0,0,0.9));
}
.circle-tile-order-badge {
    position: absolute;
    bottom: 6px;
    right: 6px;
    z-index: 2;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 2px 3px 8px rgba(0,0,0,0.5);
}
</style>
